import { ButtonHTMLAttributes, forwardRef } from "react";
import { cn } from "../../lib/cn";

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "success" | "amber";

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  loading?: boolean;
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "primary", loading, disabled, children, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(
        "inline-flex min-h-11 items-center justify-center gap-2 rounded-card px-4 py-2 text-sm font-bold transition duration-200 ease-out disabled:cursor-not-allowed disabled:opacity-55",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
        variant === "primary" &&
          "command-action hover:-translate-y-0.5 hover:shadow-lift active:translate-y-0",
        variant === "secondary" &&
          "border border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--text)] shadow-[0_1px_0_color-mix(in_oklch,var(--surface),white_35%)_inset] hover:bg-[color:var(--surface-raised)]",
        variant === "ghost" && "text-[color:var(--muted)] hover:bg-[color:var(--surface-raised)] hover:text-[color:var(--text)]",
        variant === "danger" && "bg-[color:var(--danger)] text-white hover:brightness-105",
        variant === "success" && "bg-[color:var(--success)] text-white hover:brightness-105",
        variant === "amber" && "bg-[color:var(--amber)] text-[color:var(--text)] hover:brightness-105",
        className
      )}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" aria-hidden /> : null}
      {children}
    </button>
  )
);

Button.displayName = "Button";
